import React, { FC, useEffect, useState } from "react";
import { IntervalDay } from "../../Steps/FormSecondStep";
import { DayPickerItem } from "./DayPickerItem";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper";
import { Swiper as SwiperCore } from "swiper";
import { endOfMonth, startOfDay, startOfMonth } from "date-fns";
import { DayPickerCarouselArrow } from "./DayPickerCarouselArrow";

interface DayPickerProps {
  selectedDay: Date;
  selectedMonth: Date;
  handleDayChange: (day: Date) => void;
  handleMonthChange: (newMonth: Date) => void;
  interval: IntervalDay[];
  currentSlideIndex: number;
  setCurrentSlideIndex: (index: number) => void;
}



export const DayPicker: FC<DayPickerProps> = ({
  selectedDay,
  selectedMonth,
  handleDayChange,
  handleMonthChange,
  interval,
  currentSlideIndex,
  setCurrentSlideIndex,
}) => {
  const [swiperState, setSwiperState] = useState<SwiperCore>();
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(false);


  const breakpoints = {
    0: {
      slidesPerView: 4,
      spaceBetween: 6,
      initialSlide: currentSlideIndex
    },
    951: {
      slidesPerView: 3.8,
      centeredSlides: true,
      initialSlide: currentSlideIndex == 0 ? 1 : currentSlideIndex,
      spaceBetween: 10,
    },
  };
  
  useEffect(() => {
    if (swiperState) {
      const currentDay = interval[swiperState.activeIndex];
      if (currentDay.date.getTime() < selectedMonth.getTime()) {
        const index = interval.findIndex(
          (day) => selectedMonth.getTime() === day.date.getTime()
        );
        swiperState?.slideTo(index);
      }
      const lastDayOfMonth = startOfDay(endOfMonth(selectedMonth));
      if (currentDay.date.getTime() > lastDayOfMonth.getTime()) {
        const index = interval.findIndex(
          (day) => lastDayOfMonth.getTime() === day.date.getTime()
        );
        swiperState?.slideTo(index);
      }
    }
  }, [selectedMonth.getTime()]);

  useEffect(() => {
    const index = interval.findIndex(
      (day) => day.date.getTime() === selectedDay.getTime()
    );

    swiperState?.slideTo(index);
  }, [selectedDay.getTime()]);

  const onTransitionEnd = (swiper: SwiperCore) => {
    const currentDay = interval[swiper.activeIndex];
    const currentMonth = startOfMonth(currentDay.date);
    if (currentMonth.getTime() !== selectedMonth.getTime()) {
      handleMonthChange(currentMonth);
    }
    setShowPrevArrow(swiper.activeIndex > 1);
    setShowNextArrow(swiper.activeIndex < swiper.slides.length - 1);
    setCurrentSlideIndex(swiper.activeIndex);
  };

  const goToNextSlide = () => {
    if (swiperState) {
      const currentIndex = swiperState.activeIndex;
      for (let i = currentIndex + 1; i < interval.length; i++) {
        if (interval[i].hasAppointments) {
          swiperState!.slideTo(i);
          return;
        }
      }
    }
  };

  const goToPrevSlide = () => {
    if (swiperState) {
      const currentIndex = swiperState.activeIndex;
      for (let i = currentIndex - 1; i > 0; i--) {
        if (interval[i].hasAppointments) {
          swiperState!.slideTo(i);
          return;
        }
      }
    }
  };

  return (
    <div className="mx-n2 mx-sm-1 position-relative mb-3">
      <DayPickerCarouselArrow
        onClick={goToPrevSlide}
        direction="prev"
        display={showPrevArrow}
      />
      <DayPickerCarouselArrow
        onClick={goToNextSlide}
        direction="next"
        display={showNextArrow}
      />

      <Swiper
        className="day-picker"
        breakpoints={breakpoints}
        freeMode={{ enabled: true, momentum: true, sticky: true }}
        modules={[FreeMode]}
        onTransitionEnd={onTransitionEnd}
        onSwiper={(swiper) => setSwiperState(swiper)}
      >
        {interval.map((date, i) => (
          <SwiperSlide key={i}>
            <DayPickerItem
              date={date}
              selectedDay={selectedDay}
              onClick={handleDayChange}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};
